import { useKeyboardStore } from '@/stores/keyboard'
import { useDeviceStore } from '@/stores/device'
import { ref } from 'vue'
import { INITIAL_IMAGE_HASH_ARRAY } from '@/constants/imageHashes'
/**
 * 键盘主题切换相关功能
 */
export function useKeyboardTheme() {
  const keyboardStore = useKeyboardStore()
  const deviceStore = useDeviceStore()
  // 记录右键点击的按钮的 keyCode
  const selectedKeyCode = ref(null)

  // showMenu按键的keyCode与主题图片索引的映射关系
  const SHOW_MENU_KEY_MAPPING = {
    // 页面 1-3 的按键
    73: { slot: 0, page: [1, 2, 3] },   // Ins
    74: { slot: 1, page: [1, 2, 3] },   // Home
    75: { slot: 2, page: [1, 2, 3] },   // Pgup
    76: { slot: 3, page: [1, 2, 3] },   // Del
    77: { slot: 4, page: [1, 2, 3] },   // End
    78: { slot: 5, page: [1, 2, 3] },   // Pgdn

    // 页面 4 的按键
    128: { slot: 0, page: 4 },  // fn4K1
    129: { slot: 1, page: 4 },  // fn4K2
    130: { slot: 2, page: 4 },  // fn4K3
    131: { slot: 3, page: 4 },  // fn4K4
    132: { slot: 4, page: 4 },  // fn4K5
    133: { slot: 5, page: 4 },  // fn4K6

    // 页面 5 的按键
    134: { slot: 0, page: 5 },  // fn5K1
    135: { slot: 1, page: 5 },  // fn5K2
    136: { slot: 2, page: 5 },  // fn5K3
    137: { slot: 3, page: 5 },  // fn5K4
    138: { slot: 4, page: 5 },  // fn5K5
    139: { slot: 5, page: 5 },  // fn5K6

    // 页面 6 的按键
    140: { slot: 0, page: 6 },  // fn6K1
    141: { slot: 1, page: 6 },  // fn6K2
    142: { slot: 2, page: 6 },  // fn6K3
    143: { slot: 3, page: 6 },  // fn6K4
    144: { slot: 4, page: 6 },  // fn6K5
    145: { slot: 5, page: 6 },  // fn6K6
  }
  const getSlotFromKeyCode = (keyCode) => {
    const mapping = SHOW_MENU_KEY_MAPPING[keyCode]
    return mapping?.slot ?? null
  }
  /**
   * 切换按键主题
   * @param {number} type - 主题类型 (0-4: 主题1-5, 5: 恢复默认lucency) 或 'custom' 表示自定义图片
   * @param {HTMLElement} contextMenuRef - 右键菜单的 DOM 引用
   * @param {File} imageFile - 当 type 为 'custom' 时，传入的图片文件
   */
  const chooseTheme = (type, contextMenuRef, imageFile = null) => {
    // 允许在第1页或第4页以上更改皮肤
    if (keyboardStore.currentFnPage === 1) {
      // 第1页：使用预设主题
      applyPresetTheme(type)
    } else if (keyboardStore.currentFnPage > 3) {
      // 第4页以上：支持自定义图片或预设主题
      if (type === 'custom' && imageFile) {
        // 自定义图片上传
        applyCustomImage(imageFile)
      } else if (typeof type === 'number') {
        // 预设主题
        applyPresetTheme(type)
      } else {
        // 打开文件选择器
        openImageSelector()
      }
    } else {
      // 第2页和第3页不允许更改皮肤
      console.warn('[主题切换] 仅第1页或第4页以上可以更改皮肤，当前在第' + keyboardStore.currentFnPage + '页')
    }

    // 关闭右键菜单
    if (contextMenuRef) {
      contextMenuRef.style.display = 'none'
    }
  }

  /**
   * 恢复默认主题图片
   * @param {number} type - 主题类型 (0-4: 主题1-5, 5: 恢复默认lucency)
   */
  const applyPresetTheme = (type) => {
    // 判断是否有上传图片
    const imageIndex = getSlotFromKeyCode(selectedKeyCode.value)
    const img = localStorage.getItem(`fnPage${keyboardStore.currentFnPage}CustomImage_${imageIndex}`)
    if (!img) return
    const updatedKeyboard = keyboardStore.keyCodeJson.map(key => {
      // 检查是否是showMenu按键  还要清楚localstorage 里面图片
      if (key.class && key.class.includes('showMenu') && SHOW_MENU_KEY_MAPPING[key.keyCode] !== undefined) {

        if (type === 5) {
          if (key.page === keyboardStore.currentFnPage && key.keyCode === selectedKeyCode.value) {
            localStorage.removeItem(`fnPage${keyboardStore.currentFnPage}CustomImage_${imageIndex}`)
            return {
              ...key,
              img: `m${6 * (keyboardStore.currentFnPage - 4) + imageIndex + 1}`,
              customImg: '',
              isCustomImage: true,
            }
          }
        }
      }
      return key
    })

    // 使用 keyboard store 的方法更新键盘配置
    keyboardStore.updateKeyCodeJson(updatedKeyboard)
    // 发送数据给键盘
    // deviceStore.sendSwitchSkin(type)
    // 选择皮肤6，恢复为默认的lucency
    deviceStore.sendResetImg([keyboardStore.currentFnPage - 1, imageIndex])
    if (type === 5 && keyboardStore.currentFnPage > 3 && imageIndex !== null) {
      const hashKey = 'CustomImageHashArray'
      const hashArray = getImageHashArray()
      const hashIndex = (keyboardStore.currentFnPage - 4) * 6 + imageIndex
      if (hashIndex >= 0 && hashIndex < INITIAL_IMAGE_HASH_ARRAY.length) {
        hashArray[hashIndex] = INITIAL_IMAGE_HASH_ARRAY[hashIndex]
        localStorage.setItem(hashKey, JSON.stringify(hashArray))
      }
    }
    console.log(`[主题切换] 已在第 ${keyboardStore.currentFnPage} 页保存主题 ${type}`)
  }

  /**
   * 打开本地图片选择器
   */
  const openImageSelector = () => {
    // 创建隐藏的文件输入元素
    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.accept = 'image/*'
    fileInput.style.display = 'none'

    fileInput.addEventListener('change', (event) => {
      const file = event.target.files?.[0]
      if (file) {
        applyCustomImage(file)
      }
    })

    // 触发文件选择
    document.body.appendChild(fileInput)
    fileInput.click()
    document.body.removeChild(fileInput)
  }

  /**
   * 将图片转换为 JPG 格式
   * @param {string} dataUrl - 图片的 data URL
   * @returns {Promise<string>} JPG 格式的 data URL
   */
  const convertToJpg = async (dataUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')

        // 绘制白色背景（处理透明区域）
        ctx.fillStyle = '#FFFFFF'
        ctx.fillRect(0, 0, canvas.width, canvas.height)

        // 绘制图片
        ctx.drawImage(img, 0, 0)

        // 输出为 JPG 格式（质量0.9）
        const jpgDataUrl = canvas.toDataURL('image/jpeg', 0.9)
        console.log(`[图片转换] 已将图片转换为 JPG 格式`)
        resolve(jpgDataUrl)
      }
      img.onerror = () => {
        reject(new Error('[图片转换] 图片加载失败'))
      }
      img.src = dataUrl
    })
  }

  /**
   * ✨ 优化版本：一次加载图片生成两个版本
   * 方案2：单次加载 + 两次绘制 (性能提升 60-70%)
   * @param {string} dataUrl - 图片的 data URL
   * @returns {Promise<{display: string, device: string}>} 包含两个版本的对象
   */
  const cropImageBoth = async (dataUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image()

      img.onload = () => {
        // 计算裁剪参数（只计算一次）
        const sourceSize = Math.min(img.width, img.height)
        const sourceX = (img.width - sourceSize) / 2
        const sourceY = (img.height - sourceSize) / 2

        /**
         * 内部函数：生成单个版本
         * @param {boolean} rotateRight - 是否向右旋转90度
         * @returns {string} 裁剪后的 JPG 图片 data URL
         */
        const createCroppedVersion = (rotateRight) => {
          const canvas = document.createElement('canvas')
          canvas.width = 64
          canvas.height = 64
          const ctx = canvas.getContext('2d')

          // 绘制白色背景（处理透明区域）
          ctx.fillStyle = '#FFFFFF'
          ctx.fillRect(0, 0, 64, 64)

          if (rotateRight) {
            // 向右旋转90度
            ctx.save()
            ctx.translate(32, 32)
            ctx.rotate(Math.PI / 2)
            ctx.drawImage(
              img,
              sourceX,
              sourceY,
              sourceSize,
              sourceSize,
              -32,
              -32,
              64,
              64
            )
            ctx.restore()
          } else {
            // 不旋转，直接绘制
            ctx.drawImage(img, sourceX, sourceY, sourceSize, sourceSize, 0, 0, 64, 64)
          }

          return canvas.toDataURL('image/jpeg', 0.8)
        }

        // 生成两个版本
        const displayVersion = createCroppedVersion(false) // 页面显示（不旋转）
        const deviceVersion = createCroppedVersion(true)   // 设备发送（旋转90度）

        console.log('[图片裁剪] ✨ 已从单个图片高效生成两个版本 (性能优化方案2)')

        resolve({
          display: displayVersion,
          device: deviceVersion
        })
      }

      img.onerror = () => {
        reject(new Error('[图片裁剪] 图片加载失败'))
      }

      img.src = dataUrl
    })
  }
  /**
   * 将 Base64 图片向左旋转 90 度并立即销毁 Canvas 释放内存
   * @param {string} base64String - 原始图片的 Base64 字符串
   * @returns {Promise<string>} - 返回旋转后的 Base64 字符串
   */
  const rotateBase64Left90 = (base64String) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = base64String;

      img.onload = () => {
        // 1. 创建临时画布
        let canvas = document.createElement('canvas');
        let ctx = canvas.getContext('2d');

        canvas.width = img.height;
        canvas.height = img.width;

        // 2. 变换坐标并旋转
        ctx.translate(0, canvas.height);
        ctx.rotate(-90 * Math.PI / 180);

        // 3. 绘制
        ctx.drawImage(img, 0, 0);

        // 4. 导出结果
        const rotatedBase64 = canvas.toDataURL('image/png');

        // --- 关键销毁步骤 ---
        // 1. 清空画布内容，强制释放显存
        canvas.width = 0;
        canvas.height = 0;

        // 2. 移除引用，等待垃圾回收
        ctx = null;
        canvas = null;
        // --------------------

        resolve(rotatedBase64);
      };

      img.onerror = (error) => {
        reject(new Error("图片加载失败，请检查 Base64 格式是否正确"));
      };
    });
  }
  /**
   * 将图片保存到本地
   * @param {string} dataUrl - 图片的 data URL
   * @param {string} filename - 保存的文件名（可选，默认为 'image.jpg'）
   */
  const downloadImage = (dataUrl, filename = 'image.jpg') => {
    try {
      // 创建一个 <a> 标签用于下载
      const link = document.createElement('a')
      link.href = dataUrl
      link.download = filename

      // 添加到 DOM 并触发点击
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      console.log(`[图片下载] 已将图片保存为: ${filename}`)
    } catch (error) {
      console.error('[图片下载] 保存失败:', error)
    }
  }

  /**
   * 应用自定义本地图片
   * @param {File} imageFile - 图片文件
   */
  const applyCustomImage = (imageFile) => {
    if (!selectedKeyCode.value) {
      console.warn('[自定义图片] 未选择按钮')
      return
    }

    // 使用 FileReader 将图片转换为 data URL
    const reader = new FileReader()

    reader.onload = async (e) => {
      const imageDataUrl = e.target?.result
      if (!imageDataUrl) return

      // ✨ 使用优化方案2：单次加载图片，生成两个版本
      let croppedDataUrlForDisplay // 页面显示用（不旋转）
      let croppedDataUrlForDevice  // 发送给设备用（旋转90度）
      try {
        // 高效地从一个图片生成两个版本（性能提升60-70%）
        const { display, device } = await cropImageBoth(imageDataUrl)
        croppedDataUrlForDisplay = display
        croppedDataUrlForDevice = device
      } catch (error) {
        console.error('[自定义图片] 裁剪图片失败:', error)
        return
      }

      // 保存裁剪后的图片到本地
      const timestamp = new Date().getTime()
      const keyCode = selectedKeyCode.value
      // downloadImage(croppedDataUrlForDisplay, `keyboard_custom_${keyCode}_${timestamp}.jpg`)

      // 更新键盘配置 - 使用无旋转的版本用于显示
      const updatedKeyboard = keyboardStore.keyCodeJson.map(key => {
        // 只更新右键点击的那个按钮
        if (key.keyCode === selectedKeyCode.value) {
          return {
            ...key,
            customImg: croppedDataUrlForDisplay, // 使用无旋转版本显示在页面上
            isCustomImage: true,
          }
        }
        return key
      })

      // 使用 keyboard store 的方法更新键盘配置
      keyboardStore.updateKeyCodeJson(updatedKeyboard)

      // 保存自定义图片到 localStorage（按 keyCode 保存）
      // 存储无旋转版本用于页面显示
      const key = getSlotFromKeyCode(selectedKeyCode.value)
      const keyCodeImageKey = `fnPage${keyboardStore.currentFnPage}CustomImage_${key}`
      try {
        localStorage.setItem(keyCodeImageKey, croppedDataUrlForDisplay)
        console.log(`[自定义图片] 已保存按钮 ${selectedKeyCode.value} 的自定义图片到第 ${keyboardStore.currentFnPage} 页 (页面显示版本)`)
      } catch (error) {
        console.warn('[自定义图片] localStorage 存储失败（可能超出大小限制）:', error)
      }

      // 发送旋转后的图片给设备（分包发送）
      try {
        const imageData = dataUrlToUint8Array(croppedDataUrlForDevice) // 使用旋转版本发送给设备

        // 生成图片的 CRC32 hash
        const imageHash = generateImageHash(imageData)

        // 将hash存储到localStorage数组中
        if (imageHash) {
          saveImageHash(keyboardStore.currentFnPage, key, imageHash)
          console.log(`[自定义图片] 已保存hash - page${keyboardStore.currentFnPage}[${key - 1}]: ${imageHash}`)
        }

        const success = await sendImageInPackets(imageData, key, 0) // 发送设备需要从0开始
        // 随后进行发送宏录制数据

        if (!success) {
          console.warn('[自定义图片] 图片分包发送失败')
        } else {
          console.log('[自定义图片] 已将旋转后的图片发送给设备')
        }
      } catch (error) {
        console.warn('[自定义图片] 转换或发送图片失败:', error)
      }

      console.log(`[自定义图片] 已为按钮 ${selectedKeyCode.value} 在第 ${keyboardStore.currentFnPage} 页应用图片: ${imageFile.name}`)
    }

    reader.onerror = () => {
      console.error('[自定义图片] 读取文件失败')
    }

    reader.readAsDataURL(imageFile)
  }
  /**
   * CRC32 校验和计算
   * @param {Uint8Array} data - 二进制数据
   * @returns {string} - 8字符的16进制CRC32值
   */
  const calculateCRC32 = (data) => {
    let crc = 0xffffffff
    const polynomial = 0xedb88320

    for (let i = 0; i < data.length; i++) {
      crc ^= data[i]
      for (let j = 0; j < 8; j++) {
        crc = (crc >>> 1) ^ (polynomial * (crc & 1))
      }
    }

    crc = (crc ^ 0xffffffff) >>> 0  // 关键：加 >>> 0
    return ('0000000' + crc.toString(16)).slice(-8)  // 用slice确保8位
  }

  /**
   * 使用CRC32生成图片的校验和
   * @param {Uint8Array} imageData - 图片的二进制数据
   * @returns {string} - 8字符的16进制CRC32值
   */
  const generateImageHash = (imageData) => {
    try {
      const hashHex = calculateCRC32(imageData)
      console.log(`[图片Hash] 生成CRC32: ${hashHex}`)
      return hashHex
    } catch (error) {
      console.error('[图片Hash] 生成hash失败:', error)
      return null
    }
  }

  /**
   * 将图片数据分包发送给设备
   * 第一个包：元数据 | 后续包：图片数据
   *
   * 传输协议：
   * 第一个包（包含Header）：
   *   Header(9字节) + MetaData(7字节) + Padding
   *   = [0x43, 0x52, 0x54, 0x00, 0x00, 0x4c, 0x4f, 0x47, metaData] + 填充到1023字节
   *   通过 sendImgToDevice('meta', metaPacket) 发送
   *
   * 后续数据包（不包含Header）：
   *   直接发送图片数据，每包最多 1024 字节
   *   通过 sendImgToDevice('data', dataPacket) 发送
   *
   * 第一个包数据结构（7字节）:
   * 字节 0-3: 图片大小（小端序）
   * 字节 4:   页码（1-6）
   * 字节 5:   按键编号（keyCode）
   * 字节 6:   松开按下标识（0=按下, 1=松开）
   *
   * @param {Uint8Array} imageData - 图片的二进制数据
   * @param {number} keyCode - 按键的 keyCode
   * @param {number} pressFlag - 按下/松开标识
   */
  const sendImageInPackets = async (imageData, keyCode, pressFlag = 0) => {
    try {
      // 第一个包：元数据包（7字节）
      const metaPacket = new Uint8Array(7)

      // 前4字节：图片大小（小端序）
      const imageSize = Number(imageData.length)
      metaPacket[3] = imageSize & 0xFF
      metaPacket[2] = (imageSize >> 8) & 0xFF
      metaPacket[1] = (imageSize >> 16) & 0xFF
      metaPacket[0] = (imageSize >> 24) & 0xFF

      // 第5字节：页码（1-6）
      metaPacket[4] = (Number(keyboardStore.currentFnPage) - 1) & 0xFF

      // 第6字节：按键编号（keyCode）
      metaPacket[5] = keyCode & 0xFF

      // 第7字节：松开按下标识
      metaPacket[6] = pressFlag & 0xFF

      // 发送元数据包（type='meta' 会添加 Header）
      console.log(`[图片分包] 发送元数据包 - 图片大小: ${imageSize} 字节，keyCode: ${keyCode}`)
      await deviceStore.sendImgToDevice('meta', metaPacket)

      // 等待一小段时间，确保设备收到元数据包
      await new Promise(resolve => setTimeout(resolve, 50))

      // 后续包：图片数据包（不包含Header）
      // 每个包最多 1024 字节
      const maxPacketSize = deviceStore.transmitMode === '250byte' ? 249 : 1023
      let offset = 0
      let packetIndex = 0
      const imagePackets = []
      // 调试用：只模拟丢 1 次包（命中后自动失效，后续补发正常发送）
      const SIMULATE_PACKET_LOSS = false
      const SIMULATED_LOST_PACKET = 2
      let hasSimulatedPacketLoss = false

      while (offset < imageData.length) {
        packetIndex++
        const chunkSize = Math.min(maxPacketSize, imageData.length - offset)
        const is250ByteMode = deviceStore.transmitMode === '250byte'
        const packetPayload = imageData.subarray(offset, offset + chunkSize)
        const dataPacket = new Uint8Array(is250ByteMode ? chunkSize + 1 : chunkSize)
        if (is250ByteMode) {
          imagePackets.push(new Uint8Array(packetPayload))
          dataPacket[0] = packetIndex & 0xFF
          dataPacket.set(packetPayload, 1)
        } else {
          dataPacket.set(packetPayload, 0)
        }

        console.log(`[图片分包] 发送数据包 #${packetIndex} - 偏移: ${offset}，大小: ${chunkSize} 字节`)
        // if (is250ByteMode && SIMULATE_PACKET_LOSS && !hasSimulatedPacketLoss && packetIndex === SIMULATED_LOST_PACKET) {
        //   hasSimulatedPacketLoss = true
        //   console.warn(`[图片分包] 模拟丢包：跳过发送数据包 #${packetIndex}`)
        //   offset += chunkSize
        //   continue
        // }
        // 发送图片数据（type='data' 不添加 Header）
        await deviceStore.sendImgToDevice('data', dataPacket)

        // 包之间的延迟，防止设备处理不过来
        if (offset + chunkSize < imageData.length) {
          await new Promise(resolve => setTimeout(resolve, 30))
        }

        offset += chunkSize
      }
      if (deviceStore.transmitMode === '250byte') {
        const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))
        const waitImageAck = async (timeoutMs = 1200, intervalMs = 40) => {
          const start = Date.now()
          while (Date.now() - start < timeoutMs) {
            const ackResult = deviceStore.consumeImageAckResult()
            if (ackResult !== null) {
              return ackResult
            }
            await sleep(intervalMs)
          }
          return null
        }
        const retransmitMissingPackets = async (missingPackets) => {
          for (const packetNum of missingPackets) {
            const packetIndexForStore = packetNum - 1
            if (packetIndexForStore < 0 || packetIndexForStore >= imagePackets.length) {
              console.warn(`[图片分包] 丢包序号超出范围: ${packetNum}`)
              continue
            }
            const payload = imagePackets[packetIndexForStore]
            const retransmitPacket = new Uint8Array(payload.length + 1)
            retransmitPacket[0] = packetNum & 0xFF
            retransmitPacket.set(payload, 1)
            await deviceStore.sendImgToDevice('data', retransmitPacket)
            await sleep(30)
          }
        }

        // 首轮：发送结束信令并等待ACK
        deviceStore.resetImageAckState()
        await deviceStore.sendImgOver()
        let ackResult = await waitImageAck()
        if (ackResult === null) {
          throw new Error('[图片分包] 等待首轮图片ACK超时')
        }

        // 后续：若ACK包含丢包，只重发丢包；不重复发送 sendImgOver
        let retransmitRound = 0
        const maxRetransmitRounds = 60
        while (Array.isArray(ackResult) && ackResult.length > 0 && retransmitRound < maxRetransmitRounds) {
          retransmitRound++
          console.warn(`[图片分包] 收到丢包ACK，准备重传: ${ackResult.join(',')} (第 ${retransmitRound} 轮)`)

          // 开启下一轮ACK窗口：重传丢包后再次发送结束信令，触发设备重新验包
          deviceStore.resetImageAckState()
          await retransmitMissingPackets(ackResult)
          await deviceStore.sendImgOver()

          ackResult = await waitImageAck()
          if (ackResult === null) {
            throw new Error(`[图片分包] 重传后等待ACK超时（第 ${retransmitRound} 轮）`)
          }
        }

        if (!Array.isArray(ackResult) || ackResult.length > 0) {
          throw new Error('[图片分包] 图片ACK确认失败，仍存在未恢复丢包')
        }
      }

      console.log(`[图片分包] 完成 - 共发送 ${packetIndex + 1} 个包（1个元数据 + ${packetIndex}个数据包）`)
      return true
    } catch (error) {
      console.error('[图片分包] 发送失败:', error)
      return false
    }
  }
  const dataUrlToUint8Array = (dataUrl) => {
    // 移除 data URL 前缀 (e.g., "data:image/jpeg;base64,")
    const base64String = dataUrl.split(',')[1]
    if (!base64String) {
      throw new Error('无效的 data URL 格式')
    }

    // 将 base64 转换为二进制字符串
    const binaryString = atob(base64String)

    // 将二进制字符串转换为 Uint8Array
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }

    return bytes
  }

  /**
   * 从 localStorage 恢复自定义图片
   */
  const restoreCustomImage = () => {
    if (keyboardStore.currentFnPage <= 3) {
      return
    }

    try {
      // 恢复每个 showMenu 按钮的自定义图片
      const updatedKeyboard = keyboardStore.keyCodeJson.map(key => {
        if (key.class && key.class.includes('showMenu') && SHOW_MENU_KEY_MAPPING[key.keyCode] !== undefined) {
          // 尝试从 localStorage 获取这个按钮的自定义图片
          const keyCodeImageKey = `fnPage${keyboardStore.currentFnPage}CustomImage_${key.keyCode}`
          const savedImageDataUrl = localStorage.getItem(keyCodeImageKey)

          if (savedImageDataUrl) {
            return {
              ...key,
              img: savedImageDataUrl,
              isCustomImage: true
            }
          }
        }
        return key
      })
      keyboardStore.updateKeyCodeJson(updatedKeyboard)
      console.log(`[自定义图片] 已从 localStorage 恢复第 ${keyboardStore.currentFnPage} 页的所有自定义图片`)
    } catch (error) {
      console.warn('[自定义图片] 恢复失败:', error)
    }
  }

  /**
   * 判断按键是否应该显示文本
   * @param {Object} item - 按键数据对象
   * @returns {boolean} 是否显示文本
   */
  const shouldShowKeyText = (item) => {
    // 如果是图标类型，不显示文本
    if (item.isIcon) return false
    if (item.isCustomImage) return false
    // 如果没有自定义图片，显示文本
    if (!item.img) return true

    // 如果是主题图片（theme1-5）或自定义图片，不显示文本
    if (item.img.startsWith('theme') || item.isCustomImage) return false

    // data URL 格式的自定义图片也不显示文本
    if (item.img.startsWith('data:')) return false

    // 其他情况显示文本
    return true
  }

  /**
   * 从 localStorage 恢复保存的主题（仅第1页）
   * 应该在键盘数据加载完成后调用
   */
  const restoreTheme = () => {
    // 第1页：恢复预设主题
    if (keyboardStore.currentFnPage === 1) {
      // 从 localStorage 读取保存的主题
      // const savedTheme = localStorage.getItem('fnPage1Theme')
      const savedTheme = deviceStore.lightData.style
      if (savedTheme === null) {
        console.log('[主题恢复] 没有保存的主题配置')
        return
      }

      const themeType = parseInt(savedTheme, 10)
      if (isNaN(themeType) || themeType < 0 || themeType > 5) {
        console.warn('[主题恢复] 无效的主题类型:', savedTheme)
        return
      }

      console.log(`[主题恢复] 正在恢复主题 ${themeType}`)

      // 应用主题（不需要关闭右键菜单）
      const updatedKeyboard = keyboardStore.keyCodeJson.map(key => {
        // 检查是否是showMenu按键
        if (key.class && key.class.includes('showMenu') && SHOW_MENU_KEY_MAPPING[key.keyCode] !== undefined) {
          const imageIndex = SHOW_MENU_KEY_MAPPING[key.keyCode]

          if (themeType >= 0 && themeType <= 4) {
            // 选择主题1-5
            return {
              ...key,
              img: `theme${themeType + 1}-${imageIndex}`
            }
          } else if (themeType === 5) {
            // 选择皮肤6，恢复为默认的lucency
            return {
              ...key,
              img: 'lucency'
            }
          }
        }
        return key
      })

      // 使用 keyboard store 的方法更新键盘配置
      keyboardStore.updateKeyCodeJson(updatedKeyboard)
      console.log(`[主题恢复] 主题 ${themeType} 已恢复`)
    } else if (keyboardStore.currentFnPage > 3) {
      // 第4页以上：恢复自定义图片
      restoreCustomImage()
    }
  }

  /**
   * 获取保存的图片hash数组
   * @returns {Array<string|null>} - hash数组 [crc1, crc2, ..., crc18]
   *   索引 0-5: 第4页第1-6个槽位
   *   索引 6-11: 第5页第1-6个槽位
   *   索引 12-17: 第6页第1-6个槽位
   */
  const getImageHashArray = () => {
    const key = 'CustomImageHashArray'
    const stored = localStorage.getItem(key)
    if (!stored) {
      return new Array(18).fill(null)
    }
    try {
      return JSON.parse(stored)
    } catch (error) {
      console.warn(`[图片Hash] 解析hash数组失败: ${error}`)
      return new Array(18).fill(null)
    }
  }

  /**
   * 获取单个图片hash
   * @param {number} page - 当前Fn页码 (4, 5, 6)
   * @param {number} slot - 按键槽位 (1-6)
   * @returns {string|null} - 保存的hash值，如果不存在返回null
   */
  const getImageHash = (page, slot) => {
    const hashArray = getImageHashArray()
    const index = (page - 4) * 6 + (slot - 1)
    return hashArray[index] || null
  }

  /**
   * 保存图片hash到统一数组
   * @param {number} page - 当前Fn页码 (4, 5, 6)
   * @param {number} slot - 按键槽位 (1-6)
   * @param {string} hash - CRC值
   */
  const saveImageHash = (page, slot, hash) => {
    const hashArray = getImageHashArray()
    const index = (page - 3) * 6 + (slot)
    hashArray[index] = hash
    const key = 'CustomImageHashArray'
    localStorage.setItem(key, JSON.stringify(hashArray))
    console.log(`[图片Hash] 已保存 [${index}] page${page}-slot${slot}: ${hash}`)
  }

  /**
   * 验证设备发送的CRC数组是否匹配本地保存的CRC
   * @param {Array<string|null>} deviceCrcArray - 设备发送的CRC数组，长度18
   * @returns {Array<number>} - 不匹配的索引数组，如 [1, 7, 12]，全部匹配返回空数组 []
   */
  const verifyImageHash = (deviceCrcArray) => {
    const localArray = getImageHashArray()
    const mismatchedIndices = []

    for (let i = 0; i < 18; i++) {
      const localCrc = localArray[i]
      const deviceCrc = deviceCrcArray[i]

      // 比对逻辑：如果其中一个不存在，认为不匹配
      if (localCrc !== deviceCrc) {
        mismatchedIndices.push(i)
      }
    }

    if (mismatchedIndices.length === 0) {
      console.log('[图片Hash验证] ✅ 所有18个CRC都匹配')
    } else {
      console.log(`[图片Hash验证] ❌ 不匹配索引: ${mismatchedIndices.join(', ')}`)
    }

    return mismatchedIndices
  }

  /**
   * 删除保存的图片hash
   * @param {number} page - 当前Fn页码
   * @param {number} slot - 按键槽位 (1-6)
   */
  const deleteImageHash = (page, slot) => {
    const hashArray = getImageHashArray()
    const index = (page - 3) * 6 + (slot)
    hashArray[index] = null
    const key = 'CustomImageHashArray'
    localStorage.setItem(key, JSON.stringify(hashArray))
    console.log(`[图片Hash] 已删除hash - [${index}] page${page}-slot${slot}`)
  }

  /**
   * 获取所有18个hash数组
   * @returns {Array<string|null>} - 所有hash数组，共18个元素
   */
  const getAllImageHashArray = () => {
    return getImageHashArray()
  }

  /**
   * 清空所有hash
   */
  const clearAllImageHash = () => {
    const key = 'CustomImageHashArray'
    localStorage.removeItem(key)
    console.log(`[图片Hash] 已清空所有18个hash`)
  }

  /**
   * 获取某页的hash数组 (6个)
   * @param {number} page - 当前Fn页码 (4, 5, 6)
   * @returns {Array<string|null>} - 该页的6个hash
   */
  const getPageImageHashArray = (page) => {
    const hashArray = getImageHashArray()
    const startIndex = (page - 4) * 6
    return hashArray.slice(startIndex, startIndex + 6)
  }

  return {
    selectedKeyCode,
    chooseTheme,
    shouldShowKeyText,
    restoreTheme,
    openImageSelector,
    applyCustomImage,
    restoreCustomImage,
    sendImageInPackets,
    convertToJpg,
    downloadImage,
    dataUrlToUint8Array,
    calculateCRC32,
    rotateBase64Left90,
    // 单个图片hash相关
    generateImageHash,
    getImageHash,
    getImageHashArray,
    saveImageHash,
    verifyImageHash,
    deleteImageHash,
    getAllImageHashArray,
    clearAllImageHash,
    getPageImageHashArray
  }
}
